'use client';

import React, { useState } from 'react';
import { ExtendedArchiMateDiagram, Team } from '@/lib/archimate/extended-types';
import {
  createTeam,
  assignOwnership,
  analyzeConwaysLaw,
  generateRACIMatrix,
  detectUnownedElements,
  detectOwnershipConflicts,
  exportTeamsToCSV,
} from '@/lib/archimate/team-utils';
import { Users, GitBranch, Building2, AlertTriangle, X, Plus, Download } from 'lucide-react';
import { PanelHelpDialog, HelpIconButton } from './PanelHelpDialog';

interface TeamPanelProps {
  diagram: ExtendedArchiMateDiagram;
  selectedElementId?: string;
  onUpdate: (diagram: ExtendedArchiMateDiagram) => void;
  onClose: () => void;
  onSelectElement: (elementId: string) => void;
}

export default function TeamPanel({
  diagram,
  selectedElementId,
  onUpdate,
  onClose,
  onSelectElement,
}: TeamPanelProps) {
  const [viewMode, setViewMode] = useState<'overview' | 'teams' | 'ownership' | 'raci' | 'conway'>('overview');
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);
  const [isEditingOwnership, setIsEditingOwnership] = useState(false);
  const [newTeamForm, setNewTeamForm] = useState({
    name: '',
    type: 'engineering' as 'engineering' | 'product' | 'operations' | 'security' | 'data' | 'platform',
    mission: '',
  });
  const [showHelp, setShowHelp] = useState(false);

  // Calculate overview data
  const unownedElements = detectUnownedElements(diagram);
  const conflicts = detectOwnershipConflicts(diagram);
  const conwayAnalysis = analyzeConwaysLaw(diagram);
  const raciMatrix = generateRACIMatrix(diagram);

  const handleCreateTeam = () => {
    if (!newTeamForm.name.trim()) return;

    const newTeam = createTeam(
      newTeamForm.name,
      newTeamForm.mission || `${newTeamForm.type} team`,
      [], // members - empty initially
      '' // lead - empty initially
    );

    const updatedDiagram = {
      ...diagram,
      teams: [...diagram.teams, newTeam],
    };

    onUpdate(updatedDiagram);
    setNewTeamForm({ name: '', type: 'engineering', mission: '' });
    setIsCreatingTeam(false);
  };

  const handleDeleteTeam = (teamId: string) => {
    const updatedDiagram = {
      ...diagram,
      teams: diagram.teams.filter((t) => t.id !== teamId),
      elementMetadata: { ...diagram.elementMetadata },
    };

    // Remove ownership references
    Object.keys(updatedDiagram.elementMetadata).forEach((elementId) => {
      const metadata = updatedDiagram.elementMetadata[elementId];
      if (metadata.ownership?.team === teamId) {
        delete metadata.ownership;
      }
    });

    onUpdate(updatedDiagram);
    if (selectedTeam?.id === teamId) {
      setSelectedTeam(null);
    }
  };

  const handleAssignOwnership = (elementId: string, teamId: string) => {
    const team = diagram.teams.find((t) => t.id === teamId);
    if (!team) return;

    const ownership = {
      team: team.name,
      owner: team.lead || '',
      slackChannel: '',
      repository: '',
      raci: {
        responsible: team.members,
        accountable: team.lead || '',
        consulted: [],
        informed: [],
      },
    };

    const updatedDiagram = assignOwnership(diagram, elementId, ownership);
    onUpdate(updatedDiagram);
  };

  const handleUnassignOwnership = (elementId: string) => {
    const updatedDiagram = {
      ...diagram,
      elementMetadata: {
        ...diagram.elementMetadata,
        [elementId]: {
          ...diagram.elementMetadata[elementId],
        },
      },
    };

    delete updatedDiagram.elementMetadata[elementId].ownership;
    onUpdate(updatedDiagram);
  };

  const handleExportCSV = () => {
    const csv = exportTeamsToCSV(diagram);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${diagram.name}-teams.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderOverview = () => {
    const totalTeams = diagram.teams.length;
    const totalElements = diagram.elements.length;
    const ownedElements = diagram.elements.filter(
      (el) => diagram.elementMetadata[el.id]?.ownership
    ).length;
    const ownershipCoverage = totalElements > 0 ? ((ownedElements / totalElements) * 100).toFixed(1) : '0';

    return (
      <div className="space-y-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-4 bg-cream/5 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-accent" />
              <span className="text-sm text-foreground-tertiary">Teams</span>
            </div>
            <div className="text-3xl font-bold text-foreground">{totalTeams}</div>
          </div>

          <div className="p-4 bg-cream/5 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-accent" />
              <span className="text-sm text-foreground-tertiary">Coverage</span>
            </div>
            <div className="text-3xl font-bold text-foreground">{ownershipCoverage}%</div>
            <div className="text-xs text-foreground-tertiary mt-1">
              {ownedElements} of {totalElements} elements
            </div>
          </div>
        </div>

        {/* Issues */}
        {(unownedElements.length > 0 || conflicts.length > 0) && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-500" />
              Issues
            </h4>

            {unownedElements.length > 0 && (
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium text-yellow-400 text-sm">
                      {unownedElements.length} Unowned Elements
                    </div>
                    <div className="text-xs text-foreground-tertiary mt-1">
                      Elements without team ownership
                    </div>
                  </div>
                  <button
                    onClick={() => setViewMode('ownership')}
                    className="px-3 py-1 text-xs bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 rounded transition-colors"
                  >
                    Assign
                  </button>
                </div>
              </div>
            )}

            {conflicts.length > 0 && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium text-red-400 text-sm">
                      {conflicts.length} Ownership Conflicts
                    </div>
                    <div className="text-xs text-foreground-tertiary mt-1">
                      Multiple teams claiming ownership
                    </div>
                  </div>
                  <button
                    onClick={() => setViewMode('ownership')}
                    className="px-3 py-1 text-xs bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded transition-colors"
                  >
                    Review
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Conway's Law Analysis */}
        {conwayAnalysis.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
              <GitBranch className="w-4 h-4 text-accent" />
              Conway&apos;s Law Analysis
            </h4>

            <div className="p-3 bg-accent-muted border border-border rounded-lg">
              <div className="text-sm text-foreground-secondary mb-2">
                <span className="font-medium text-accent">
                  {conwayAnalysis.length}
                </span>{' '}
                potential misalignments detected
              </div>
              <div className="text-xs text-foreground-tertiary">
                System dependencies don&apos;t match team structure
              </div>
              <button
                onClick={() => setViewMode('conway')}
                className="mt-3 px-3 py-1.5 text-xs bg-accent-muted hover:bg-accent/30 text-accent rounded transition-colors"
              >
                View Analysis
              </button>
            </div>
          </div>
        )}

        {/* Team List */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-foreground">Teams</h4>
            <button
              onClick={() => setViewMode('teams')}
              className="text-xs text-accent hover:text-accent transition-colors"
            >
              Manage →
            </button>
          </div>

          {diagram.teams.length === 0 ? (
            <div className="p-6 text-center text-foreground-tertiary text-sm">
              No teams defined yet
            </div>
          ) : (
            <div className="space-y-2">
              {diagram.teams.slice(0, 5).map((team) => {
                const ownedCount = diagram.elements.filter(
                  (el) => diagram.elementMetadata[el.id]?.ownership?.team === team.name
                ).length;

                return (
                  <div
                    key={team.id}
                    className="p-3 bg-cream/5 hover:bg-cream/10 rounded-lg transition-colors cursor-pointer"
                    onClick={() => {
                      setSelectedTeam(team);
                      setViewMode('teams');
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-foreground text-sm">{team.name}</div>
                        <div className="text-xs text-foreground-tertiary">{team.description}</div>
                      </div>
                      <div className="text-sm text-foreground-secondary">{ownedCount} elements</div>
                    </div>
                  </div>
                );
              })}
              {diagram.teams.length > 5 && (
                <div className="text-center text-xs text-foreground-tertiary pt-2">
                  + {diagram.teams.length - 5} more teams
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTeams = () => {
    return (
      <div className="space-y-4">
        {/* Create Team Form */}
        {isCreatingTeam ? (
          <div className="p-4 bg-cream/5 border border-border rounded-lg space-y-3">
            <h4 className="text-sm font-medium text-foreground">Create New Team</h4>
            <input
              type="text"
              value={newTeamForm.name}
              onChange={(e) => setNewTeamForm({ ...newTeamForm, name: e.target.value })}
              placeholder="Team name"
              className="w-full px-3 py-2 bg-background border border-border text-foreground rounded text-sm focus:outline-none focus:border-accent"
            />
            <select
              value={newTeamForm.type}
              onChange={(e) =>
                setNewTeamForm({
                  ...newTeamForm,
                  type: e.target.value as any,
                })
              }
              className="w-full px-3 py-2 bg-background border border-border text-foreground rounded text-sm focus:outline-none focus:border-accent"
            >
              <option value="engineering">Engineering</option>
              <option value="product">Product</option>
              <option value="operations">Operations</option>
              <option value="security">Security</option>
              <option value="data">Data</option>
              <option value="platform">Platform</option>
            </select>
            <textarea
              value={newTeamForm.mission}
              onChange={(e) => setNewTeamForm({ ...newTeamForm, mission: e.target.value })}
              placeholder="Mission (optional)"
              rows={3}
              className="w-full px-3 py-2 bg-background border border-border text-foreground rounded text-sm focus:outline-none focus:border-accent resize-none"
            />
            <div className="flex gap-2">
              <button
                onClick={handleCreateTeam}
                className="flex-1 px-4 py-2 bg-accent hover:bg-accent-hover text-accent-foreground font-medium rounded text-sm transition-colors"
              >
                Create Team
              </button>
              <button
                onClick={() => {
                  setIsCreatingTeam(false);
                  setNewTeamForm({ name: '', type: 'engineering', mission: '' });
                }}
                className="px-4 py-2 bg-cream/10 hover:bg-cream/20 text-foreground rounded text-sm transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setIsCreatingTeam(true)}
            className="w-full px-4 py-3 bg-accent-muted hover:bg-accent-muted border border-border text-accent rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create New Team
          </button>
        )}

        {/* Team List */}
        <div className="space-y-3">
          {diagram.teams.map((team) => {
            const ownedElements = diagram.elements.filter(
              (el) => diagram.elementMetadata[el.id]?.ownership?.team === team.name
            );

            const isSelected = selectedTeam?.id === team.id;

            return (
              <div
                key={team.id}
                className={`p-4 rounded-lg border transition-all cursor-pointer ${
                  isSelected
                    ? 'bg-accent-muted border-accent'
                    : 'bg-cream/5 border-border hover:bg-cream/10'
                }`}
                onClick={() => setSelectedTeam(isSelected ? null : team)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="font-medium text-foreground mb-1">{team.name}</div>
                    {team.description && (
                      <div className="text-sm text-foreground-secondary mb-3">{team.description}</div>
                    )}
                    <div className="text-sm text-foreground-secondary">
                      Owns <span className="font-medium text-accent">{ownedElements.length}</span>{' '}
                      elements
                    </div>
                    {team.members && team.members.length > 0 && (
                      <div className="text-xs text-foreground-tertiary mt-1">
                        {team.members.length} member{team.members.length !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteTeam(team.id);
                    }}
                    className="p-2 hover:bg-red-500/20 text-foreground-tertiary hover:text-red-400 rounded transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {isSelected && ownedElements.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="text-xs font-medium text-foreground-secondary mb-2">Owned Elements</div>
                    <div className="space-y-1.5">
                      {ownedElements.slice(0, 10).map((el) => (
                        <div
                          key={el.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectElement(el.id);
                          }}
                          className="flex items-center justify-between text-sm p-2 bg-cream/5 hover:bg-cream/10 rounded transition-colors"
                        >
                          <span className="text-foreground-secondary">{el.name}</span>
                          <span className="text-xs text-foreground-tertiary capitalize">
                            {el.type.replace(/-/g, ' ')}
                          </span>
                        </div>
                      ))}
                      {ownedElements.length > 10 && (
                        <div className="text-xs text-foreground-tertiary text-center pt-1">
                          + {ownedElements.length - 10} more
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {diagram.teams.length === 0 && !isCreatingTeam && (
            <div className="p-8 text-center text-foreground-tertiary text-sm">
              No teams defined yet. Create a team to get started.
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderOwnership = () => {
    return (
      <div className="space-y-4">
        {/* Unowned Elements */}
        {unownedElements.length > 0 && (
          <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            <div className="flex items-center gap-2 text-yellow-400 text-sm font-medium mb-1">
              <AlertTriangle className="w-4 h-4" />
              {unownedElements.length} Unowned Elements
            </div>
            <div className="text-xs text-foreground-tertiary">
              Assign teams to improve ownership clarity
            </div>
          </div>
        )}

        {/* Elements List */}
        <div className="space-y-2">
          {diagram.elements.map((element) => {
            const ownership = diagram.elementMetadata[element.id]?.ownership;
            const team = ownership ? diagram.teams.find((t) => t.name === ownership.team) : null;

            return (
              <div key={element.id} className="p-3 bg-cream/5 rounded-lg space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div
                      className="font-medium text-foreground text-sm cursor-pointer hover:text-accent transition-colors"
                      onClick={() => onSelectElement(element.id)}
                    >
                      {element.name}
                    </div>
                    <div className="text-xs text-foreground-tertiary capitalize">
                      {element.type.replace(/-/g, ' ')} • {element.layer}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {team ? (
                    <>
                      <div className="flex-1 px-3 py-1.5 bg-cream/10 rounded text-sm">
                        <span className="text-foreground-tertiary">Team: </span>
                        <span className="text-foreground font-medium">{team.name}</span>
                      </div>
                      <button
                        onClick={() => handleUnassignOwnership(element.id)}
                        className="px-3 py-1.5 text-xs bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded transition-colors"
                      >
                        Unassign
                      </button>
                    </>
                  ) : (
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAssignOwnership(element.id, e.target.value);
                        }
                      }}
                      defaultValue=""
                      className="flex-1 px-3 py-1.5 bg-background border border-border text-foreground rounded text-sm focus:outline-none focus:border-accent"
                    >
                      <option value="">Assign to team...</option>
                      {diagram.teams.map((team) => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            );
          })}

          {diagram.elements.length === 0 && (
            <div className="p-8 text-center text-foreground-tertiary text-sm">No elements in diagram</div>
          )}
        </div>
      </div>
    );
  };

  const renderRACI = () => {
    return (
      <div className="space-y-4">
        <div className="p-3 bg-cream/5 rounded-lg">
          <div className="text-xs text-foreground-tertiary space-y-1">
            <div>
              <span className="font-medium text-foreground">R</span> - Responsible (does the work)
            </div>
            <div>
              <span className="font-medium text-foreground">A</span> - Accountable (makes decisions)
            </div>
            <div>
              <span className="font-medium text-foreground">C</span> - Consulted (provides input)
            </div>
            <div>
              <span className="font-medium text-foreground">I</span> - Informed (kept updated)
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {Object.entries(raciMatrix).map(([elementId, raci]) => {
            const element = diagram.elements.find((el) => el.id === elementId);
            if (!element) return null;

            return (
              <div key={elementId} className="p-3 bg-cream/5 rounded-lg">
                <div
                  className="font-medium text-foreground text-sm mb-3 cursor-pointer hover:text-accent transition-colors"
                  onClick={() => onSelectElement(elementId)}
                >
                  {element.name}
                </div>

                <div className="space-y-2">
                  {(['responsible', 'accountable', 'consulted', 'informed'] as const).map(
                    (role) => {
                      const value = raci[role];
                      if (!value) return null;

                      const names = Array.isArray(value) ? value : [value];
                      if (names.length === 0) return null;

                      const teams = names
                        .map((name) => diagram.teams.find((t) => t.name === name))
                        .filter(Boolean) as Team[];

                      if (teams.length === 0) return null;

                      return (
                        <div key={role} className="flex items-start gap-3 text-sm">
                          <div className="w-16 text-foreground-tertiary capitalize flex-shrink-0">
                            {role[0].toUpperCase()}
                          </div>
                          <div className="flex-1 flex flex-wrap gap-1.5">
                            {teams.map((team) => (
                              <span
                                key={team.id}
                                className="px-2 py-0.5 bg-cream/10 text-foreground-secondary rounded text-xs"
                              >
                                {team.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            );
          })}

          {Object.keys(raciMatrix).length === 0 && (
            <div className="p-8 text-center text-foreground-tertiary text-sm">
              No ownership data available. Assign teams to elements to generate RACI matrix.
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderConway = () => {
    return (
      <div className="space-y-4">
        <div className="p-3 bg-cream/5 rounded-lg">
          <div className="text-sm text-foreground-secondary mb-2">
            <span className="font-medium text-accent">Conway&apos;s Law:</span> Organizations design
            systems that mirror their communication structure
          </div>
          <div className="text-xs text-foreground-tertiary">
            Misalignments indicate elements owned by different teams but closely connected
          </div>
        </div>

        {conwayAnalysis.length === 0 ? (
          <div className="p-6 text-center text-foreground-tertiary text-sm">
            No misalignments detected. System structure aligns with team structure.
          </div>
        ) : (
          <div className="space-y-3">
            {conwayAnalysis.map((misalignment) => {
              const sourceEl = misalignment.source;
              const targetEl = misalignment.target;
              const sourceTeam = diagram.teams.find((t) => t.name === misalignment.sourceTeam);
              const targetTeam = diagram.teams.find((t) => t.name === misalignment.targetTeam);

              if (!sourceEl || !targetEl) return null;

              return (
                <div
                  key={`${sourceEl.id}-${targetEl.id}`}
                  className="p-4 bg-accent-muted border border-border rounded-lg"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <GitBranch className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-medium text-accent text-sm mb-1">
                        Cross-Team Dependency
                      </div>
                      <div className="text-xs text-foreground-tertiary">
                        {misalignment.reason}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div
                      className="flex items-center justify-between p-2 bg-background/50 rounded cursor-pointer hover:bg-background/70 transition-colors"
                      onClick={() => onSelectElement(misalignment.source.id)}
                    >
                      <div>
                        <div className="text-sm text-foreground">{sourceEl.name}</div>
                        {sourceTeam && (
                          <div className="text-xs text-foreground-tertiary">Team: {sourceTeam.name}</div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-center">
                      <div className="text-accent text-xs">↓</div>
                    </div>

                    <div
                      className="flex items-center justify-between p-2 bg-background/50 rounded cursor-pointer hover:bg-background/70 transition-colors"
                      onClick={() => onSelectElement(misalignment.target.id)}
                    >
                      <div>
                        <div className="text-sm text-foreground">{targetEl.name}</div>
                        {targetTeam && (
                          <div className="text-xs text-foreground-tertiary">Team: {targetTeam.name}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 text-xs text-foreground-tertiary">
                    Consider: Should these elements be owned by the same team, or do the teams need
                    closer coordination?
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed top-0 right-0 h-full w-[600px] bg-background/95 backdrop-blur border-l border-border shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-accent" />
          <h2 className="text-xl font-bold text-foreground">Team Ownership</h2>
          <HelpIconButton onClick={() => setShowHelp(true)} />
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-cream/10 rounded-lg transition-colors"
        >
          <X className="w-5 h-5 text-foreground" />
        </button>
      </div>

      {/* Help Dialog */}
      {showHelp && (
        <PanelHelpDialog
          panelKey="team"
          onClose={() => setShowHelp(false)}
        />
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2 p-4 border-b border-border overflow-x-auto">
        <button
          onClick={() => setViewMode('overview')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
            viewMode === 'overview'
              ? 'bg-accent text-accent-foreground'
              : 'bg-cream/10 text-foreground hover:bg-cream/20'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setViewMode('teams')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
            viewMode === 'teams'
              ? 'bg-accent text-accent-foreground'
              : 'bg-cream/10 text-foreground hover:bg-cream/20'
          }`}
        >
          Teams
        </button>
        <button
          onClick={() => setViewMode('ownership')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
            viewMode === 'ownership'
              ? 'bg-accent text-accent-foreground'
              : 'bg-cream/10 text-foreground hover:bg-cream/20'
          }`}
        >
          Ownership
        </button>
        <button
          onClick={() => setViewMode('raci')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
            viewMode === 'raci' ? 'bg-accent text-accent-foreground' : 'bg-cream/10 text-foreground hover:bg-cream/20'
          }`}
        >
          RACI
        </button>
        <button
          onClick={() => setViewMode('conway')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
            viewMode === 'conway'
              ? 'bg-accent text-accent-foreground'
              : 'bg-cream/10 text-foreground hover:bg-cream/20'
          }`}
        >
          Conway
        </button>
        <button
          onClick={handleExportCSV}
          className="ml-auto p-2 hover:bg-cream/10 rounded-lg transition-colors"
          title="Export to CSV"
        >
          <Download className="w-4 h-4 text-foreground" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {viewMode === 'overview' && renderOverview()}
        {viewMode === 'teams' && renderTeams()}
        {viewMode === 'ownership' && renderOwnership()}
        {viewMode === 'raci' && renderRACI()}
        {viewMode === 'conway' && renderConway()}
      </div>
    </div>
  );
}
